import type { Facility } from '../types';
import { facilities } from './mockData';
import { copyDefaultTo } from './platformService';
import { respond } from './respond';
import { isReal, loadFacilities, loadFacility } from './store';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { copyDefaultToReal } from './platformService';
import { db } from '../lib/firebase';
import { scope } from './scope';
import { seedNewClinic } from './programmeService';
import { addAudit, type Actor } from './auditService';
import { callServer } from './server';

export async function getFacilities(): Promise<Facility[]> {
  if (isReal()) {
    // Clinic users can only read their own clinic; super admin reads the list.
    return scope().role === 'super_admin' ? loadFacilities() : [await loadFacility(scope().facilityId)];
  }
  return respond(facilities);
}

export async function getFacility(id: string): Promise<Facility> {
  if (isReal()) return loadFacility(id);
  const f = facilities.find((x) => x.id === id);
  if (!f) throw new Error(`Facility ${id} not found`);
  return respond(f);
}

const slug = (s: string) => s.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 30);

export async function createFacility(data: Omit<Facility, 'id'>, actor?: Actor): Promise<Facility> {
  if (isReal()) {
    // Readable id, used in the clinic's login link: /c/<id>/login (e.g. sharma-meerut).
    const base = [slug(data.shortName || data.displayNameEn).split('-')[0], slug(data.city)].filter(Boolean).join('-') || 'clinic';
    let id = base;
    for (let n = 2; (await getDoc(doc(db!, 'facilities', id))).exists(); n++) id = `${base}-${n}`;
    // Always starts in "setup": the rules allow nothing else, and it only goes live after doctor sign-off.
    await setDoc(doc(db!, 'facilities', id), JSON.parse(JSON.stringify({ ...data, status: 'setup' })));
    await copyDefaultToReal(id);
    // New clinics start with Suraksha's ready-made master script and reviewed ranges, waiting for their doctor.
    await seedNewClinic(id, data.services ?? {}, actor).catch(() => undefined);
    return loadFacility(id);
  }
  const f: Facility = { ...data, id: `fac-${Date.now()}` };
  facilities.push(f);
  copyDefaultTo(f.id);
  return respond(f);
}

/**
 * Change a clinic's calling hours: a clinic setting its admin or doctor agrees to, with a reason, audit-logged
 * (before → after). Real mode: only the server may do it (functions/src/callingHours.ts); the rules refuse it from a browser.
 */
export async function setCallingHours(id: string, hours: { start: string; end: string }, reason: string, actor?: Actor): Promise<void> {
  if (isReal()) {
    await callServer('setCallingHours', { facilityId: id, start: hours.start, end: hours.end, reason });
    return;
  }
  const f = facilities.find((x) => x.id === id);
  if (!f) throw new Error(`Facility ${id} not found`);
  const before = `${f.callingHours.start}–${f.callingHours.end}`;
  f.callingHours = { ...hours };
  addAudit(actor, 'facility.calling_hours_changed', id, id, `${before} → ${hours.start}–${hours.end} IST · ${reason}`);
  await respond(undefined);
}

/** Calling hours never travel with an ordinary save: they go through setCallingHours. */
function withoutHours(patch: Partial<Facility>): Partial<Facility> {
  const { callingHours: _h, ...rest } = patch as Partial<Facility> & { callingHoursChange?: unknown };
  delete (rest as { callingHoursChange?: unknown }).callingHoursChange;
  return rest;
}

export async function updateFacility(id: string, input: Partial<Facility>): Promise<Facility> {
  const patch = withoutHours(input);
  if (isReal()) {
    // The rules decide who may change what (e.g. only doctors sign off, nobody activates an unready clinic).
    const { id: _ignore, ...rest } = patch;
    await updateDoc(doc(db!, 'facilities', id), JSON.parse(JSON.stringify(rest)));
    return loadFacility(id);
  }
  const f = facilities.find((x) => x.id === id);
  if (!f) throw new Error(`Facility ${id} not found`);
  Object.assign(f, patch);
  if (f.status === 'active' && !Object.values(f.goLive).every(Boolean)) f.status = f.goLive && Object.values(f.goLive).some(Boolean) ? 'paused' : 'setup';
  return respond(f);
}
